import React, { useEffect, useState } from "react";
import { Skeleton } from "antd";
import BannersApi from "../../../../api-services/banners-api.service";
import type { Banner } from "../../../../interfaces/banner.interface";
import type { TravelMode } from "../Homepage";
import ScrollCarousel from "./ScrollCarousel";
import SectionHeader from "./SectionHeader";
import useBreakpoint from "../../../../utils/useBreakpoint";

const bannersApi = new BannersApi();

interface Props {
  mode: TravelMode;
}

// "Limited Time Promo" — wide promotional banner cards. Uses the shared
// fixed-width scroll-snap track (like the other sections) so the banners always
// stay on a single row and scroll horizontally, including on mobile.
export default function LimitedTimePromo({ mode }: Props) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useBreakpoint();

  useEffect(() => {
    setLoading(true);
    // Slide#tab_types speaks series/cruise/ground, not the toggle's tour/cruise/ground.
    const tabType = mode === "cruise" ? "cruise" : mode === "ground" ? "ground" : "series";
    bannersApi.getSlides("limited_promo", tabType)
      .then(setBanners)
      .catch(() => setBanners([]))
      .finally(() => setLoading(false));
  }, [mode]);

  if (!loading && banners.length === 0) return null;

  return (
    <section className="v2-section" id="limited-promo-v2">
      <SectionHeader title="Limited Time Promo" viewAllHref="/web/limited-promo" />
      <ScrollCarousel trackClassName="v2-promo__track" step={isMobile ? 344 : 396}>
        {loading && [1, 2, 3].map((placeholder) => (
          <div key={placeholder} className="v2-promo-card">
            <Skeleton.Image active />
          </div>
        ))}

        {!loading && banners.map((banner) => {
          const image = (
            <picture>
              <source media="(max-width: 599px)" srcSet={banner.mobileImage || banner.desktopImage} />
              <img src={banner.desktopImage} alt={banner.title || "Promotion"} />
            </picture>
          );

          return (
            <div key={`${banner.title}-${banner.sortOrder}`} className="v2-promo-card">
              {banner.url ? (
                <a href={banner.url} target={banner.target} rel="nofollow">
                  {image}
                </a>
              ) : image}
            </div>
          );
        })}
      </ScrollCarousel>
    </section>
  );
}
